import { FC } from 'react';
import classNames from 'classnames';

import { CloudinaryImage } from 'components/common/CloudinaryImage';
import { ExpandableText } from 'components/common/ExpandableText';
import { Link } from 'components/common/Link';
import { Button } from 'components/design-system/Button';
import { Text } from 'components/design-system/Text/Text';
import { useTheme } from 'context/theme';
import { useViewport } from 'context/viewport';
import { pageRoutes } from 'lib/routes';

type BrandDescriptionBannerProps = {
  brandName: string;
  description: string;
  imageUrl?: string;
  brandKey: string;
};

const BrandDescriptionMobile: FC<BrandDescriptionBannerProps> = ({ brandName, description, imageUrl, brandKey }) => {
  const { isDark } = useTheme();

  return (
    <div>
      {imageUrl && (
        <CloudinaryImage
          responsive
          src={imageUrl}
          alt={`${brandName} watch image`}
          sizes='100vw'
          className='overflow-hidden border-radius-24'
        />
      )}
      <div className={`px-2 pt-3 mb-1 text-primary ${isDark ? 'text-light' : 'text-primary'}`}>
        <Text className='fs-12px font-weight-bold mt-2 riforma-medium letter-spacing-1 opacity-40'>MORE ABOUT</Text>
        <Text className='fs-24px mt-1 mb-4 line-height-24px' style={{ fontWeight: 600 }}>
          {brandName}
        </Text>
        <Text className='fs-14px mt-3 mb-4 opacity-60 line-height-20px'>{description}</Text>
        <Link href={pageRoutes.brand(brandKey)}>
          <Button
            variant={isDark ? 'light10' : 'secondary'}
            className={`w-100 p-3 border-0 ${isDark ? 'text-light' : 'text-primary'}`}
          >
            <span>Shop all {brandName}</span>
          </Button>
        </Link>
      </div>
    </div>
  );
};

const BrandDescriptionDesktop: FC<BrandDescriptionBannerProps> = ({ brandName, description, imageUrl, brandKey }) => {
  const { isDark } = useTheme();

  return (
    <>
      <div
        className={`pt-3 ml-3 ${isDark ? 'text-light' : 'text-primary'}`}
        style={{ width: '45%', paddingLeft: '15px', paddingRight: '30px' }}
      >
        <Text className='fs-28px line-height-34px mt-1'>{brandName}</Text>
        <ExpandableText
          text={description}
          lines={7}
          className={`riforma-regular fs-14px mt-3 mb-5 w-90 ${
            isDark ? 'text-light60' : 'text-secondary'
          } line-height-24px`}
          moreButtonClassName='text-success'
        />
        <Link href={pageRoutes.brand(brandKey)}>
          <Button
            variant={isDark ? 'light10' : 'secondary'}
            className={`p-3 mb-1 border-0 ${isDark ? 'text-light' : 'text-primary'}`}
            style={{ width: '88%' }}
          >
            <span>Shop all {brandName}</span>
          </Button>
        </Link>
      </div>
      <div className='position-relative pt-1' style={{ width: '55%' }}>
        {imageUrl && (
          <CloudinaryImage
            responsive
            style={{ borderRadius: '36px' }}
            src={imageUrl}
            alt={`${brandName} watch image`}
            sizes='30vw'
          />
        )}
      </div>
    </>
  );
};

export const BrandDescriptionBanner: FC<BrandDescriptionBannerProps> = ({
  brandName,
  description,
  imageUrl,
  brandKey
}) => {
  const { isMobileSmall } = useViewport();
  const BrandBanner = isMobileSmall ? BrandDescriptionMobile : BrandDescriptionDesktop;
  const { isDark } = useTheme();

  return (
    <div
      className={classNames('mx-3 mx-lg-0 mt-4 d-flex border rounded-3 position-relative p-3', {
        'border-light20': isDark
      })}
      style={{ minHeight: '200px' }}
    >
      <BrandBanner brandName={brandName} description={description} imageUrl={imageUrl as string} brandKey={brandKey} />
    </div>
  );
};
